"use client";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React, { useState } from "react";
import { Button as AriaButton } from "react-aria-components";
import { Avatar, Button, Text } from "ui-components";

import { DeleteConversationConfirmationModal } from "@/src/components/modals/DeleteConversationConfirmationModal";
import { ConversationResponseEntity } from "@/src/hooks/api/generated";
import { useChat } from "@/src/hooks/useChat";
import { useChatFunctions } from "@/src/hooks/useChatFunctions";
import { useNavigate } from "@/src/hooks/useNavigate";

dayjs.extend(relativeTime);

export interface IConservationItem {
  conversation: ConversationResponseEntity;
}

export function ConservationItem({
  conversation,
}: IConservationItem): JSX.Element {
  const [
    isOpenDeleteChatConfirmationModal,
    setIsOpenDeleteChatConfirmationModal,
  ] = useState(false);
  const { pushQueryParam } = useNavigate();
  const { selectedConversation } = useChat();
  const { changeSearchConnectionStatus, getOtherConversationUser } =
    useChatFunctions();
  const { lastMessage } = conversation;

  const firstRecipient = getOtherConversationUser(
    conversation.conversationRecipients
  );

  const userStatus = firstRecipient?.online ? "online" : "offline";

  const isUnread = conversation.isUnread ? "base-bold" : "base-thin";

  const handleClickConversation = (): void => {
    if (firstRecipient) {
      pushQueryParam({
        key: "recipient",
        value: firstRecipient?.id.toString(),
      });
    }
    changeSearchConnectionStatus(false);
  };
  return (
    <>
      <DeleteConversationConfirmationModal
        conversationId={conversation.id}
        isOpen={isOpenDeleteChatConfirmationModal}
        onOpenChange={setIsOpenDeleteChatConfirmationModal}
      />
      <AriaButton
        onPress={handleClickConversation}
        className={`outline-none p-2 hover:bg-brand-primary-10 ${
          selectedConversation?.userId === firstRecipient?.id
            ? "bg-brand-primary-10"
            : ""
        }`}
      >
        <div className="flex flex-row flex-1 w-full gap-2">
          <Avatar
            alt="user profile"
            src={firstRecipient?.userTaxonomy?.image?.public_id}
            size="xs"
            status={userStatus}
          />
          <div className="flex flex-col flex-1 p-1">
            <div className="flex justify-between">
              <Text>{firstRecipient?.first_name}</Text>
              {conversation.isBlocked ? (
                <Button
                  size="xxs"
                  className="max-h-3"
                  intent="danger"
                  onClick={() => setIsOpenDeleteChatConfirmationModal(true)}
                >
                  <Text variant="small" intent="white">
                    Delete
                  </Text>
                </Button>
              ) : (
                <Text variant={isUnread}>
                  {dayjs(lastMessage?.createdAt).format("D MMM YY")}
                  {conversation.isUnread}
                </Text>
              )}
            </div>
            <div className="w-50">
              {conversation.isBlocked ? (
                <Text
                  variant="small"
                  intent="secondary"
                  className="line-clamp-2"
                >
                  This member has been removed from your profile.
                </Text>
              ) : lastMessage.messageAttachments[0]?.asset.original_name ? (
                <Text className="line-clamp-1" variant={isUnread}>
                  {lastMessage.messageAttachments[0]?.asset.original_name}
                </Text>
              ) : (
                <Text className="line-clamp-2" variant={isUnread}>
                  {lastMessage?.content}
                </Text>
              )}
            </div>
          </div>
        </div>
      </AriaButton>
    </>
  );
}
